// Motor de bitácora UCI (Fase 4, Tanda 3) — `ChecklistTurno`: checklist con
// frecuencia libre + AM/PM/HS (cuidados generales de 113, procedimientos de
// enfermería de 114, plan de cuidados de 115...), un solo componente
// parametrizado por catálogo de ítems en vez de repetido a mano en cada
// formulario. Todavía sin conectar a ningún formulario real — ver
// `TablaHoraria24h.tsx` para la nota completa.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

export interface ItemChecklistTurno {
  readonly clave: string;
  readonly etiqueta: string;
  /** Renglón "Otros": la enfermera escribe el nombre del cuidado (el papel deja renglones en blanco al final de la lista). */
  readonly nombreEditable?: boolean;
}

export interface RespuestaChecklistTurno {
  /** Solo en los ítems `nombreEditable`. */
  readonly nombre?: string;
  readonly frecuencia: string;
  readonly am: boolean;
  readonly pm: boolean;
  readonly hs: boolean;
}

export type EstadoChecklistTurno = Readonly<Record<string, RespuestaChecklistTurno>>;

function respuestaVacia(): RespuestaChecklistTurno {
  return { frecuencia: '', am: false, pm: false, hs: false };
}

/** Estado inicial — una respuesta vacía por cada ítem del catálogo. */
export function estadoInicialChecklistTurno(items: readonly ItemChecklistTurno[]): EstadoChecklistTurno {
  return Object.fromEntries(items.map((i) => [i.clave, respuestaVacia()]));
}

/** Actualiza un solo ítem (frecuencia y/o AM/PM/HS) — pura, exportada para test. */
export function actualizarItemChecklistTurno(estado: EstadoChecklistTurno, clave: string, cambios: Partial<RespuestaChecklistTurno>): EstadoChecklistTurno {
  return { ...estado, [clave]: { ...(estado[clave] ?? respuestaVacia()), ...cambios } };
}

/** Ítems del catálogo marcados en al menos un turno o con frecuencia cargada — lista dispersa para cuando cada formulario real arme su `Par[]`/diagrama de respaldo. */
export function itemsConDatoChecklistTurno(items: readonly ItemChecklistTurno[], estado: EstadoChecklistTurno): readonly ItemChecklistTurno[] {
  return items.filter((i) => {
    const r = estado[i.clave];
    return !!r && ((r.nombre ?? '').trim() !== '' || r.frecuencia.trim() !== '' || r.am || r.pm || r.hs);
  });
}

/**
 * `Par[]` de un checklist: un par por ítem con dato. Los ítems `nombreEditable` salen como "<etiqueta>: <nombre>" (o solo la etiqueta si no
 * se nombró) y, si solo tienen nombre, con "sin registro" (un cuidado nombrado sin frecuencia/turno sigue siendo un dato).
 */
export function paresDeItemsChecklistTurno(items: readonly ItemChecklistTurno[], estado: EstadoChecklistTurno): { pregunta: string; respuesta: string }[] {
  let ordinalNombrable = 0;
  return items.flatMap((i) => {
    if (i.nombreEditable) ordinalNombrable += 1;
    const nombre = i.nombreEditable ? (estado[i.clave]?.nombre ?? '').trim() : '';
    const resumen = resumenItemChecklistTurno(estado, i.clave);
    if (resumen === '' && nombre === '') return [];
    // Los renglones "otros" sin nombre se numeran para no repetir la misma pregunta.
    const pregunta = !i.nombreEditable ? i.etiqueta : nombre !== '' ? `${i.etiqueta}: ${nombre}` : `${i.etiqueta} ${ordinalNombrable}`;
    return [{ pregunta, respuesta: resumen || 'sin registro' }];
  });
}

/** Resumen compacto "frecuencia · AM,PM,HS" de un ítem, para el `Par[]` de respaldo — cadena vacía si el ítem no tiene ningún dato. */
export function resumenItemChecklistTurno(estado: EstadoChecklistTurno, clave: string): string {
  const r = estado[clave];
  if (!r) return '';
  const turnos = [r.am ? 'AM' : undefined, r.pm ? 'PM' : undefined, r.hs ? 'HS' : undefined].filter((t): t is string => t !== undefined).join(',');
  return [r.frecuencia.trim(), turnos].filter((p) => p !== '').join(' · ');
}

export interface ChecklistTurnoProps {
  readonly items: readonly ItemChecklistTurno[];
  readonly estado: EstadoChecklistTurno;
  readonly onChange: (estado: EstadoChecklistTurno) => void;
}

/** Checklist frecuencia+AM/PM/HS genérico — reusado por cuidados generales/oxigenación/procedimientos de enfermería de los 3 formularios. */
export function ChecklistTurno({ items, estado, onChange }: ChecklistTurnoProps): JSX.Element {
  const { t } = useTranslation();

  function actualizar(clave: string, cambios: Partial<RespuestaChecklistTurno>): void {
    onChange(actualizarItemChecklistTurno(estado, clave, cambios));
  }

  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="text-xs font-medium text-hce-muted">
            <th className="p-1 text-left">{t('formularios.uci.checklistTurno.item')}</th>
            <th className="p-1 text-left">{t('formularios.uci.checklistTurno.frecuencia')}</th>
            <th className="p-1 text-center">{t('formularios.uci.checklistTurno.am')}</th>
            <th className="p-1 text-center">{t('formularios.uci.checklistTurno.pm')}</th>
            <th className="p-1 text-center">{t('formularios.uci.checklistTurno.hs')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const r = estado[item.clave] ?? respuestaVacia();
            return (
              <tr key={item.clave} className="border-t border-border">
                <td className="p-1 text-hce-ink">
                  {item.nombreEditable ? (
                    <Input className="h-7 w-48 text-xs" value={r.nombre ?? ''} placeholder={item.etiqueta} aria-label={item.etiqueta} onChange={(e) => actualizar(item.clave, { nombre: e.target.value })} />
                  ) : (
                    item.etiqueta
                  )}
                </td>
                <td className="p-1">
                  <Input className="h-7 w-24 text-xs" value={r.frecuencia} onChange={(e) => actualizar(item.clave, { frecuencia: e.target.value })} />
                </td>
                <td className="p-1 text-center">
                  <Checkbox checked={r.am} onCheckedChange={(v) => actualizar(item.clave, { am: v === true })} aria-label={`${item.etiqueta} — ${t('formularios.uci.checklistTurno.am')}`} />
                </td>
                <td className="p-1 text-center">
                  <Checkbox checked={r.pm} onCheckedChange={(v) => actualizar(item.clave, { pm: v === true })} aria-label={`${item.etiqueta} — ${t('formularios.uci.checklistTurno.pm')}`} />
                </td>
                <td className="p-1 text-center">
                  <Checkbox checked={r.hs} onCheckedChange={(v) => actualizar(item.clave, { hs: v === true })} aria-label={`${item.etiqueta} — ${t('formularios.uci.checklistTurno.hs')}`} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
